import * as React from 'react'
import type { Topic, TopicPage } from './api-types'

/**
 * The editorial topic that fills the feature banner. Same-origin, so a plain
 * root-relative path (the session cookie rides along).
 */
export const HERO_TOPIC_ENDPOINT = '/api/3.0/content/topic/hero'

/**
 * The editorial topic promoted inside the browse page — one topic with its
 * `related_movies`, shown as a highlighted row under "continue watching".
 */
export const HIGHLIGHT_TOPIC_ENDPOINT = '/api/3.0/content/topic/highlight'

/**
 * The editorial topics offered for browsing at the foot of the page — a *page* of
 * topics ({@link TopicPage}), not a single one, so it goes through
 * {@link useTopicPage}.
 */
export const DISCOVER_TOPICS_ENDPOINT = '/api/3.0/content/topic/discover'

/** One topic by id — what the topic detail page loads. */
export const topicEndpoint = (id: string): string => `/api/3.0/content/topic/${id}`

/**
 * The in-app route of a topic's detail page (`/thema/:id`, registered by the browse
 * pilet). Lives next to the endpoint so the banner, the rows and the page itself
 * all spell the topic's address one way — the styleguide's own components link
 * here too, which is why it can't sit in a pilet.
 */
export const topicPath = (id: string): string => `/thema/${id}`

export interface TopicState {
  /** The topic, or `null` while loading and when the domain has none. */
  topic: Topic | null;
  loading: boolean;
}

export interface TopicPageState {
  /** The topics on the first page — empty while loading and when there are none. */
  topics: Topic[];
  loading: boolean;
}

/**
 * Fetch a topic endpoint, treating `404` as the documented "no content" answer
 * rather than an error. Shared by {@link useTopic} and {@link useTopicPage}; both
 * degrade to their empty value on any other failure too, after logging — an
 * editorial extra must never take the page down with it.
 */
const fetchTopics = <T> (endpoint: string): Promise<T | null> =>
  fetch(endpoint).then(res => {
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`${endpoint} answered ${res.status}`)
    return res.json() as Promise<T>
  })

/**
 * Load a single editorial topic (the hero banner, the browse highlight) from the
 * content API. Resolves to `topic: null` when the domain has none configured.
 */
export const useTopic = (endpoint: string): TopicState => {
  const [state, setState] = React.useState<TopicState>({ topic: null, loading: true })

  React.useEffect(() => {
    // Guard state writes behind `active` so a response landing after unmount doesn't
    // set state on a gone component.
    let active = true
    setState({ topic: null, loading: true })
    fetchTopics<Topic>(endpoint)
      .then(topic => { if (active) setState({ topic, loading: false }) })
      .catch(error => {
        console.error(error)
        if (active) setState({ topic: null, loading: false })
      })
    return () => { active = false }
  }, [endpoint])

  return state
}

/**
 * Load a page of editorial topics (the browse "discover" list) from the content
 * API. Only the first page is requested — the callers show a fixed set at the foot
 * of a page, so nothing pages through the rest yet.
 */
export const useTopicPage = (endpoint: string): TopicPageState => {
  const [state, setState] = React.useState<TopicPageState>({ topics: [], loading: true })

  React.useEffect(() => {
    let active = true
    setState({ topics: [], loading: true })
    fetchTopics<TopicPage>(endpoint)
      .then(page => { if (active) setState({ topics: page?.results ?? [], loading: false }) })
      .catch(error => {
        console.error(error)
        if (active) setState({ topics: [], loading: false })
      })
    return () => { active = false }
  }, [endpoint])

  return state
}
